import { useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FaTrash, FaEdit } from 'react-icons/fa';
function PrivacyPolicyManagement() {
  const { data: policies, loading: policiesLoading, fetchData: fetchPolicies } = useApi('/privacy-policy/all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    title: '',
    content: '',
  });
  const [policyErrors, setPolicyErrors] = useState({});

  const validatePolicyForm = () => {
    const errors = {};
    if (!policyForm.title.trim()) errors.title = 'Title is required';
    if (!policyForm.content.trim()) errors.content = 'Content is required';
    setPolicyErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePolicyInputChange = (e) => {
    const { name, value } = e.target;
    setPolicyForm({ ...policyForm, [name]: value });
    setPolicyErrors({ ...policyErrors, [name]: '' });
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setPolicyForm({ ...policyForm, content: data });
    setPolicyErrors({ ...policyErrors, content: '' });
  };

  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    if (!validatePolicyForm()) {
      toast.error('Please fix form errors');
      return;
    }
    try {
      const formData = {
        title: policyForm.title,
        content: policyForm.content,
      };

      if (editingPolicyId) {
        await apiService.request('put', `/privacy-policy/${editingPolicyId}`, formData);
        toast.success('Privacy policy updated successfully');
      } else {
        await apiService.post('/privacy-policy', formData);
        toast.success('Privacy policy added successfully');
      }
      fetchPolicies();
      setIsModalOpen(false);
      setPolicyForm({
        title: '',
        content: '',
      });
      setEditingPolicyId(null);
      setPolicyErrors({});
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const handleEditPolicy = (item) => {
    setPolicyForm({
      title: item.title || '',
      content: item.content || '',
    });
    setEditingPolicyId(item._id);
    setIsModalOpen(true);
    setPolicyErrors({});
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this privacy policy?')) return;
    try {
      await apiService.delete(`/privacy-policy/${policyId}`);
      toast.success('Privacy policy deleted successfully');
      fetchPolicies();
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const policyColumns = [
    { key: 'title', label: 'Title', render: (item) => item.title || 'N/A' },
    { key: 'updatedAt', label: 'Last Updated', render: (item) => new Date(item.updatedAt).toLocaleString() || 'N/A' },
  ];

  const policyActions = [
    {
      label: 'Edit',
      className: 'text-yellow-500 hover:text-yellow-600',
      icon: <FaEdit />,
      onClick: handleEditPolicy,
    },
    {
      label: 'Delete',
      className: 'text-red-500 hover:text-red-600',
      icon: <FaTrash />,
      onClick: (item) => handleDeletePolicy(item._id),
    },
  ];

  return (
    <div className="">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Privacy Policy Management</h2>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold dark:text-white">Privacy Policies</h3>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Add Privacy Policy
        </button>
      </div>
      {policiesLoading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">Loading...</div>
      ) : !policies || policies.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">No privacy policies found.</div>
      ) : (
        <div className="card">
          <Table columns={policyColumns} data={policies || []} actions={policyActions} />
        </div>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPolicyForm({
            title: '',
            content: '',
          });
          setEditingPolicyId(null);
          setPolicyErrors({});
        }}
        title={editingPolicyId ? 'Edit Privacy Policy' : 'Add Privacy Policy'}
        className="max-w-full lg:max-w-4xl mx-4"
      >
        <div className="max-h-[80vh] overflow-y-auto p-4">
          <form onSubmit={handlePolicySubmit}>
            <div className="grid grid-cols-1 gap-4">
              <FormInput
                label="Title"
                name="title"
                value={policyForm.title}
                onChange={handlePolicyInputChange}
                required
                placeholder="Enter privacy policy title"
                error={policyErrors.title}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
                <div className="mt-1 ckeditor-container">
                  <CKEditor
                    editor={ClassicEditor}
                    data={policyForm.content}
                    onChange={handleEditorChange}
                    config={{
                      toolbar: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        'link',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'blockQuote',
                        '|',
                        'undo',
                        'redo',
                      ],
                      language: 'en',
                    }}
                  />
                </div>
                {policyErrors.content && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{policyErrors.content}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setPolicyForm({
                    title: '',
                    content: '',
                  });
                  setPolicyErrors({});
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={policiesLoading}
              >
                {editingPolicyId ? 'Update Privacy Policy' : 'Add Privacy Policy'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}

export default PrivacyPolicyManagement;